import React, { useState, useEffect } from 'react';
import CommentItem from './CommentItem';
import CommentForm from './CommentForm';

const API_BASE = process.env.REACT_APP_API_BASE;
const API_KEY = process.env.REACT_APP_API_KEY;

export default function CommentSection({ reviewId, user, initialCommentCount = 0 }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [totalCount, setTotalCount] = useState(initialCommentCount);
  const [submitting, setSubmitting] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  // Load comments when component mounts if there are any, or when section is expanded
  useEffect(() => {
    console.log('🔍 CommentSection mount check:', { initialCommentCount, commentsLength: comments.length, reviewId });
    if (initialCommentCount > 0 && comments.length === 0) {
      console.log('📥 Loading comments on mount because initialCommentCount > 0');
      loadComments();
    }
  }, [initialCommentCount]);

  useEffect(() => {
    if (expanded && comments.length === 0) {
      loadComments();
    }
  }, [expanded]);

  const loadComments = async () => {
    setLoading(true);
    try {
      const headers = {
        'X-API-Key': API_KEY,
        'Content-Type': 'application/json',
      };

      const response = await fetch(`${API_BASE}/v1/reviews/${reviewId}/comments?limit=50`, {
        headers,
      });

      if (response.ok) {
        const data = await response.json();
        setComments(data.comments);
        setTotalCount(data.total_count);
      }
    } catch (error) {
      console.error('Failed to load comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCommentSubmit = async (content, parentCommentId = null) => {
    if (!user || submitting) return;

    console.log('🚀 Submitting comment:', { content, parentCommentId, API_BASE, API_KEY: API_KEY ? 'SET' : 'MISSING', userId: user.id });

    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/v1/reviews/${reviewId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY,
          'X-User-ID': user.id,
        },
        body: JSON.stringify({ content, parent_comment_id: parentCommentId }),
      });

      if (response.ok) {
        const newComment = await response.json();
        console.log('✅ Comment created successfully:', newComment);

        if (parentCommentId) {
          // Add reply to existing parent comment
          setComments(prevComments =>
            prevComments.map(comment =>
              comment.id === parentCommentId
                ? { ...comment, replies: [...comment.replies, newComment] }
                : comment
            )
          );
        } else {
          // Add as new parent comment
          console.log('📝 Adding new parent comment to state');
          setComments(prevComments => {
            const updated = [newComment, ...prevComments];
            console.log('📝 Updated comments state:', updated);
            return updated;
          });
        }

        setTotalCount(prev => {
          const newCount = prev + 1;
          console.log('📊 Updated comment count:', newCount);
          return newCount;
        });

        // Expand section if it wasn't already and hide the add form
        if (!expanded) {
          console.log('📂 Expanding comments section');
          setExpanded(true);
        }
        setShowAddForm(false);
      } else {
        console.error('❌ Comment submission failed:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('❌ Error details:', errorText);
      }
    } catch (error) {
      console.error('Failed to submit comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCommentDelete = (commentId, isReply, parentId = null) => {
    if (isReply) {
      // Remove reply from parent
      setComments(prevComments =>
        prevComments.map(comment =>
          comment.id === parentId
            ? { ...comment, replies: comment.replies.filter(reply => reply.id !== commentId) }
            : comment
        )
      );
    } else {
      // Remove parent comment
      setComments(prevComments => prevComments.filter(comment => comment.id !== commentId));
    }
    setTotalCount(prev => Math.max(0, prev - 1));
  };

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  return (
    <div className="mt-2 pt-2 border-t border-gray-100">
      {/* Comment count button OR Add comment button - Strava style */}
      <div className="flex items-center gap-3">
        {totalCount > 0 && (
          <button
            onClick={toggleExpanded}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 transition-colors duration-200"
          >
            <span className="text-base">💬</span>
            <span>{totalCount} comment{totalCount !== 1 ? 's' : ''}</span>
            <span className="text-xs text-gray-400">
              {expanded ? '▲' : '▼'}
            </span>
          </button>
        )}

        {/* Add comment button - only show when not already showing form */}
        {user && !showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            className="text-sm text-indigo-600 hover:text-indigo-800 transition-colors duration-200"
          >
            Add a comment
          </button>
        )}
      </div>

      {/* Comment form - only when explicitly requested */}
      {user && showAddForm && (
        <div className="mt-3">
          <CommentForm
            onSubmit={(content) => handleCommentSubmit(content)}
            onCancel={() => setShowAddForm(false)}
            submitting={submitting}
            placeholder="Add a comment..."
            user={user}
          />
        </div>
      )}

      {/* Expanded comments section */}
      {expanded && (
        <div className="mt-3 space-y-3">
          {loading ? (
            <div className="text-sm text-gray-500">Loading comments...</div>
          ) : (
            <>
              {comments.map((comment) => (
                <CommentItem
                  key={comment.id}
                  comment={comment}
                  user={user}
                  onReply={(content) => handleCommentSubmit(content, comment.id)}
                  onDelete={() => handleCommentDelete(comment.id, false)}
                  submitting={submitting}
                />
              ))}

              {/* Add comment button at bottom when expanded */}
              {user && !showAddForm && (
                <button
                  onClick={() => setShowAddForm(true)}
                  className="text-sm text-indigo-600 hover:text-indigo-800 transition-colors duration-200"
                >
                  Add a comment
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Show sign in message when expanded but not logged in */}
      {expanded && totalCount === 0 && !user && (
        <div className="mt-3 text-sm text-gray-500">
          Sign in to leave a comment
        </div>
      )}
    </div>
  );
}